import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LifeBuoy, LogOut, ChevronDown, Menu, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isStaff = user && ["admin", "agent"].includes(user.role);
  const isCustomer = user && user.role === "customer";
  const isAdmin = user && user.role === "admin";

  const navClass = ({ isActive }) =>
    `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
      isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
    }`;

  // Group definitions for the dropdowns (staff only)
  const customersGroup = [
    { to: "/admin/users", label: "Users", testId: "nav-users-btn" },
    { to: "/subscriptions", label: "Subscriptions", testId: "nav-subscriptions-btn" },
    { to: "/reminders", label: "Reminders", testId: "nav-reminders-btn" },
  ];
  const adminGroup = [
    { to: "/admin/agents", label: "Agents", testId: "nav-admin-btn" },
    { to: "/admin/api-keys", label: "API Keys", testId: "nav-api-keys-btn" },
    { to: "/admin/gmail", label: "Incoming Mail", testId: "nav-incoming-btn" },
    { to: "/admin/settings", label: "Outgoing Mail", testId: "nav-outgoing-btn" },
  ];

  const isGroupActive = (items) => items.some((i) => location.pathname.startsWith(i.to));

  const groupTriggerClass = (active) =>
    `flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors outline-none ${
      active
        ? "bg-blue-50 text-blue-700"
        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
    }`;

  const renderGroup = (label, items, testId) => (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={testId}
        className={groupTriggerClass(isGroupActive(items))}
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[10rem]">
        {items.map((item) => (
          <DropdownMenuItem
            key={item.to}
            data-testid={item.testId}
            onSelect={() => navigate(item.to)}
            className={`cursor-pointer ${
              location.pathname.startsWith(item.to) ? "bg-blue-50 text-blue-700" : ""
            }`}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const mobileLink = (to, label, testId) => (
    <NavLink
      key={to}
      to={to}
      className={({ isActive }) =>
        `block px-3 py-2 rounded-lg text-sm font-medium ${
          isActive ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-100"
        }`
      }
      data-testid={testId}
      onClick={() => setMobileOpen(false)}
    >
      {label}
    </NavLink>
  );

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 w-full bg-white/90 backdrop-blur-md border-b border-slate-200/80"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-teal-500 grid place-items-center shadow-sm">
            <LifeBuoy className="w-5 h-5 text-white" />
          </span>
          <span className="font-display text-lg font-bold text-slate-900 tracking-tight">
            Pixel Support
          </span>
          <span className="hidden sm:inline-block ml-1 text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">
            Helpdesk
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 lg:gap-2">
          <NavLink to="/track" className={navClass} data-testid="nav-track-ticket-btn">
            Track Status
          </NavLink>

          {isCustomer && (
            <>
              <NavLink to="/submit" className={navClass} data-testid="nav-submit-ticket-btn">
                New Ticket
              </NavLink>
              <NavLink to="/my-tickets" className={navClass} data-testid="nav-my-tickets-btn">
                My Tickets
              </NavLink>
            </>
          )}

          {isStaff && (
            <>
              <NavLink to="/workspace" className={navClass} data-testid="nav-workspace-btn">
                Workspace
              </NavLink>
              {renderGroup("Customers", customersGroup, "nav-customers-menu-btn")}
              {isAdmin && renderGroup("Admin", adminGroup, "nav-admin-menu-btn")}
            </>
          )}

          {user ? (
            <div className="hidden md:flex items-center gap-2 ml-2 pl-3 border-l border-slate-200">
              <div className="text-right leading-tight">
                <div className="text-sm font-semibold text-slate-800">{user.name}</div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">{user.role}</div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                data-testid="logout-btn"
                onClick={() => { logout(); navigate("/"); }}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <NavLink
                to="/login"
                className="ml-1 px-3.5 py-1.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-all"
                data-testid="nav-agent-login-btn"
              >
                Sign In
              </NavLink>
              <NavLink
                to="/register"
                className="ml-1 px-3.5 py-1.5 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-all"
                data-testid="nav-register-btn"
              >
                Sign Up
              </NavLink>
            </>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-700 hover:bg-slate-100"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="nav-mobile-toggle-btn"
          aria-label="Toggle navigation"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden border-t border-slate-200 bg-white"
          data-testid="nav-mobile-panel"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 space-y-1">
            {mobileLink("/track", "Track Status", "nav-track-ticket-btn-mobile")}

            {isCustomer && (
              <>
                {mobileLink("/submit", "New Ticket", "nav-submit-ticket-btn-mobile")}
                {mobileLink("/my-tickets", "My Tickets", "nav-my-tickets-btn-mobile")}
              </>
            )}

            {isStaff && (
              <>
                {mobileLink("/workspace", "Workspace", "nav-workspace-btn-mobile")}

                <div className="pt-2 pb-1 px-3 text-[10px] font-mono uppercase tracking-widest text-slate-400">
                  Customers
                </div>
                {customersGroup.map((i) =>
                  mobileLink(i.to, i.label, `${i.testId}-mobile`)
                )}

                {isAdmin && (
                  <>
                    <div className="pt-2 pb-1 px-3 text-[10px] font-mono uppercase tracking-widest text-slate-400">
                      Admin
                    </div>
                    {adminGroup.map((i) =>
                      mobileLink(i.to, i.label, `${i.testId}-mobile`)
                    )}
                  </>
                )}
              </>
            )}

            <div className="pt-3 mt-2 border-t border-slate-200">
              {user ? (
                <div className="flex items-center justify-between px-3 py-2">
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-slate-800">{user.name}</div>
                    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">{user.role}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="logout-btn-mobile"
                    onClick={() => { logout(); setMobileOpen(false); navigate("/"); }}
                  >
                    <LogOut className="w-4 h-4 mr-1" /> Logout
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3">
                  <NavLink
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="flex-1 text-center px-3.5 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    data-testid="nav-agent-login-btn-mobile"
                  >
                    Sign In
                  </NavLink>
                  <NavLink
                    to="/register"
                    onClick={() => setMobileOpen(false)}
                    className="flex-1 text-center px-3.5 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="nav-register-btn-mobile"
                  >
                    Sign Up
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
